import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card,
  Divider, 
  Chip,
  IconButton
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import COLORS from '../../theme/Colors';
import { StyledButton, SmallHeightDivider } from '../../theme/Styles';

const mockLogs = [
  {
    id: 'log-1',
    date: 'Hoy, 10:30 AM',
    title: 'Observación de Trámite Añadida',
    request: 'REQ-2026-9901 (Alojamiento Turístico)',
    description: 'El analista Miguel Sánchez añadió una observación sobre el documento de No Antecedentes Judiciales.',
    type: 'observation',
    status: 'warning'
  },
  {
    id: 'log-2',
    date: 'Ayer, 03:15 PM',
    title: 'Documento Cargado por Ciudadano',
    request: 'REQ-2026-9901 (Alojamiento Turístico)',
    description: 'Carga exitosa del archivo "certificacion_pago_tasas.pdf" en la sección de anexos del trámite.',
    type: 'upload',
    status: 'success'
  },
  {
    id: 'log-3',
    date: '06 Jun 2026, 03:00 PM',
    title: 'Pre-Aprobación Emitida',
    request: 'REQ-2026-0812 (CONFOTUR Clasificación)',
    description: 'La solicitud superó la fase de revisión documental primaria. Estado cambiado a Pre-Aprobada.',
    type: 'status_change',
    status: 'success'
  },
  {
    id: 'log-4',
    date: '05 Jun 2026, 11:20 AM',
    title: 'Pago Verificado Satisfactoriamente',
    request: 'REQ-2026-0812 (CONFOTUR Clasificación)',
    description: 'Verificación del depósito de tasa de tramitación realizada a través de SIRITE (Pasarela de Pagos). Transacción: #TX-9812739.',
    type: 'payment',
    status: 'info'
  },
  {
    id: 'log-5',
    date: '28 May 2026, 11:30 AM',
    title: 'Licencia Turística Aprobada y Emitida',
    request: 'REQ-2026-0044 (Guía de Turismo)',
    description: 'El Ministerio de Turismo emitió formalmente la licencia clasificada. Firma digital adjuntada al expediente de forma oficial.',
    type: 'issuance',
    status: 'success'
  },
  {
    id: 'log-6',
    date: '25 May 2026, 09:00 AM',
    title: 'Inicio de Expediente / Creación de Solicitud',
    request: 'REQ-2026-0044 (Guía de Turismo)',
    description: 'El ciudadano inició la solicitud en línea para el trámite de renovación de licencia de guía turístico.',
    type: 'creation',
    status: 'info'
  }
];

export default function HistoryPage() {
  const [logs, setLogs] = useState(mockLogs);
  const [filterType, setFilterType] = useState('all');

  const filteredLogs = filterType === 'all' 
    ? logs 
    : logs.filter(l => l.type === filterType);

  const getLogIcon = (type) => {
    switch (type) {
      case 'observation':
        return <ErrorIcon sx={{ color: '#d97706' }} />;
      case 'upload':
        return <CheckCircleIcon sx={{ color: '#16a34a' }} />;
      case 'status_change':
        return <CheckCircleIcon sx={{ color: '#2563eb' }} />;
      case 'payment':
        return <ReceiptLongIcon sx={{ color: '#4f46e5' }} />;
      case 'issuance':
        return <CheckCircleIcon sx={{ color: '#059669' }} />;
      case 'creation':
        return <PendingIcon sx={{ color: '#64748b' }} />;
      default:
        return <HistoryIcon />;
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* Main Title */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#192873', mb: 1, fontFamily: 'Roboto, sans-serif', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HistoryIcon sx={{ fontSize: '36px', color: '#192873' }} />
          Historial de Actividad
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Roboto, sans-serif' }}>
          Monitoree el registro histórico de todas las operaciones, transacciones, cambios de estado y observaciones de sus trámites.
        </Typography>
      </Box>

      {/* Filter Tabs / Chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 4, flexWrap: 'wrap' }}>
        {[
          { value: 'all', label: 'Todo' },
          { value: 'payment', label: 'Pagos' },
          { value: 'observation', label: 'Observaciones' },
          { value: 'upload', label: 'Cargas de Anexos' },
          { value: 'status_change', label: 'Cambios de Estado' },
          { value: 'issuance', label: 'Emisiones Oficiales' }
        ].map((btn) => (
          <Chip
            key={btn.value}
            label={btn.label}
            onClick={() => setFilterType(btn.value)}
            color={filterType === btn.value ? 'primary' : 'default'}
            variant={filterType === btn.value ? 'default' : 'outlined'}
            sx={{
              fontWeight: 'bold',
              fontFamily: 'Roboto, sans-serif',
              px: 1,
              py: 2,
              borderRadius: '8px',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}
          />
        ))}
      </Box>

      {/* Timeline List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {filteredLogs.map((log, index) => (
          <Box key={log.id} sx={{ display: 'flex', gap: 3 }}>
            {/* Timeline Column Left: Icon and Line */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  width: 44, 
                  height: 44, 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'rgba(3, 13, 57, 0.04)',
                  border: '1px solid #E2E8F0',
                  zIndex: 2
                }}
              >
                {getLogIcon(log.type)}
              </Paper>
              {index < filteredLogs.length - 1 && (
                <Box sx={{ width: '2px', flex: 1, bgcolor: '#E2E8F0', my: 1, minHeight: '30px' }} />
              )}
            </Box>

            {/* Timeline Column Right: Log Card */}
            <Card 
              elevation={0} 
              sx={{ 
                flex: 1, 
                p: 3, 
                border: '1px solid #E2E8F0', 
                borderRadius: '16px', 
                bgcolor: '#FFFFFF',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 'none'
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#192873', fontFamily: 'Roboto, sans-serif' }}>
                    {log.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: COLORS.primary, fontWeight: 'bold', fontFamily: 'Roboto, sans-serif' }}>
                    Trámite: {log.request}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: '500', fontFamily: 'Roboto, sans-serif' }}>
                  {log.date}
                </Typography>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto, sans-serif', lineHeight: 1.6 }}>
                {log.description}
              </Typography>
            </Card>
          </Box>
        ))}

        {filteredLogs.length === 0 && (
          <Paper elevation={0} sx={{ border: '1px dashed #E2E8F0', borderRadius: '16px', p: 8, textAlign: 'center', bgcolor: 'rgba(3, 13, 57, 0.01)' }}>
            <HistoryIcon sx={{ fontSize: '48px', color: 'text.disabled', mb: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#192873', fontFamily: 'Roboto, sans-serif', mb: 1 }}>
              Sin registros encontrados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto, sans-serif' }}>
              No se registran eventos del tipo seleccionado en su bitácora de actividad.
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
